import * as React from "react";
import {
  Badge,
  BadgeProps,
  Button,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Spinner,
  Subtitle2,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowClockwise24Regular,
  ArrowSync24Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  DismissCircle20Regular,
  Mail20Regular,
} from "@fluentui/react-icons";
import type {
  UploadJobItemStatus,
  UploadJobItemSummary,
  UploadJobStatus,
  UploadJobSummary,
} from "../../types/sharepoint";

type JobView = "active" | "failed" | "recent";

const ACTIVE_STATUSES = new Set<UploadJobStatus>(["pending", "running"]);

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },

  header: {
    alignItems: "center",
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    justifyContent: "space-between",
  },

  title: {
    alignItems: "center",
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },

  refreshButton: {
    flexShrink: 0,
  },

  overview: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    display: "grid",
    gap: tokens.spacingHorizontalS,
    gridTemplateColumns: "repeat(3, 1fr)",
    padding: tokens.spacingHorizontalS,
  },

  overviewItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },

  overviewValue: {
    lineHeight: tokens.lineHeightBase300,
  },

  mutedText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },

  viewRow: {
    display: "grid",
    gap: tokens.spacingHorizontalXS,
    gridTemplateColumns: "repeat(3, 1fr)",
  },

  viewButton: {
    minWidth: 0,
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    maxHeight: "420px",
    overflowY: "auto",
    paddingRight: "2px",
  },

  jobItem: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalS,
  },

  activeJobItem: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalS,
  },

  jobHeader: {
    alignItems: "center",
    display: "grid",
    gap: tokens.spacingHorizontalXS,
    gridTemplateColumns: "auto 1fr auto",
  },

  jobTitleBlock: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  jobTitle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  detailRow: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
  },

  countsRow: {
    display: "grid",
    gap: tokens.spacingHorizontalXS,
    gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))",
  },

  countPill: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
  },

  itemList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    marginTop: tokens.spacingVerticalXXS,
  },

  itemRow: {
    alignItems: "start",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    display: "grid",
    gap: tokens.spacingHorizontalXS,
    gridTemplateColumns: "auto 1fr auto",
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
  },

  itemSubject: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  itemError: {
    color: tokens.colorPaletteRedForeground1,
    gridColumn: "2 / -1",
  },

  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXXS,
  },

  empty: {
    color: tokens.colorNeutralForeground3,
    padding: `${tokens.spacingVerticalM} 0`,
    textAlign: "center",
  },
});

interface RecentJobsProps {
  jobs: UploadJobSummary[];
  itemsByJobId: Record<string, UploadJobItemSummary[]>;
  expandedJobIds: Record<string, boolean>;
  loading: boolean;
  error: string;
  onRefresh: () => void;
  onToggleJob: (jobId: string) => void;
  onRetryJob: (jobId: string) => void;
  onCancelJob: (jobId: string) => void;
}

const STATUS_COLOR: Record<UploadJobStatus, BadgeProps["color"]> = {
  pending: "informative",
  running: "warning",
  completed: "success",
  failed: "danger",
  cancelled: "subtle",
};

const STATUS_LABEL: Record<UploadJobStatus, string> = {
  pending: "Pending",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

const ITEM_STATUS_COLOR: Record<UploadJobItemStatus, BadgeProps["color"]> = {
  pending: "informative",
  uploading: "warning",
  uploaded: "success",
  failed: "danger",
  skipped: "subtle",
};

function isActive(job: UploadJobSummary): boolean {
  return ACTIVE_STATUSES.has(job.status);
}

function getProgress(job: UploadJobSummary): number {
  if (job.totalItems <= 0) return 0;
  return Math.min(job.processedCount / job.totalItems, 1);
}

function getJobTitle(job: UploadJobSummary): string {
  if (isActive(job)) return `Uploading ${job.totalItems} email${job.totalItems === 1 ? "" : "s"}`;
  return `${job.totalItems} email${job.totalItems === 1 ? "" : "s"} upload`;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getViewJobs(view: JobView, jobs: UploadJobSummary[]): UploadJobSummary[] {
  if (view === "active") return jobs.filter(isActive);
  if (view === "failed") return jobs.filter((job) => job.failedCount > 0 || job.status === "failed");
  return jobs.filter((job) => !isActive(job)).slice(0, 2);
}

function getInitialView(jobs: UploadJobSummary[]): JobView {
  if (jobs.some(isActive)) return "active";
  return "recent";
}

function isTerminal(status: UploadJobStatus): boolean {
  return status === "completed" || status === "failed" || status === "cancelled";
}

export const RecentJobs: React.FC<RecentJobsProps> = ({
  jobs,
  itemsByJobId,
  expandedJobIds,
  loading,
  error,
  onRefresh,
  onToggleJob,
  onRetryJob,
  onCancelJob,
}) => {
  const styles = useStyles();
  const [view, setView] = React.useState<JobView>(() => getInitialView(jobs));

  const activeCount = jobs.filter(isActive).length;
  const failedCount = jobs.filter((job) => job.failedCount > 0 || job.status === "failed").length;
  const recentCount = jobs.filter((job) => !isActive(job)).slice(0, 2).length;
  const visibleJobs = getViewJobs(view, jobs);
  const heading = activeCount === 0 && view === "recent" ? "Recent uploads" : "Upload Progress";

  React.useEffect(() => {
    if (activeCount > 0 && view !== "active") {
      setView("active");
      return;
    }

    if (activeCount === 0 && view === "active") {
      setView("recent");
    }
  }, [activeCount, jobs, view]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.title}>
          <ArrowSync24Regular fontSize={18} />
          <Subtitle2 as="h3">{heading}</Subtitle2>
          {loading && <Spinner size="tiny" />}
        </div>
        <Button
          appearance="transparent"
          icon={<ArrowClockwise24Regular />}
          size="small"
          onClick={onRefresh}
          aria-label="Refresh jobs"
          className={styles.refreshButton}
        />
      </div>

      <div className={styles.overview}>
        <div className={styles.overviewItem}>
          <Text weight="semibold" className={styles.overviewValue}>
            {activeCount}
          </Text>
          <Text className={styles.mutedText}>Active</Text>
        </div>
        <div className={styles.overviewItem}>
          <Text weight="semibold" className={styles.overviewValue}>
            {failedCount}
          </Text>
          <Text className={styles.mutedText}>Needs attention</Text>
        </div>
        <div className={styles.overviewItem}>
          <Text weight="semibold" className={styles.overviewValue}>
            {recentCount}
          </Text>
          <Text className={styles.mutedText}>Recent</Text>
        </div>
      </div>

      <div className={styles.viewRow}>
        <Button
          size="small"
          appearance={view === "active" ? "primary" : "secondary"}
          className={styles.viewButton}
          onClick={() => setView("active")}
          disabled={activeCount === 0}
        >
          Active
        </Button>
        <Button
          size="small"
          appearance={view === "failed" ? "primary" : "secondary"}
          className={styles.viewButton}
          onClick={() => setView("failed")}
          disabled={failedCount === 0}
        >
          Failed
        </Button>
        <Button
          size="small"
          appearance={view === "recent" ? "primary" : "secondary"}
          className={styles.viewButton}
          onClick={() => setView("recent")}
          disabled={recentCount === 0}
        >
          Recent
        </Button>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {!loading && jobs.length === 0 && (
        <Text className={styles.empty} size={200}>
          No upload jobs yet.
        </Text>
      )}

      {jobs.length > 0 && visibleJobs.length === 0 && (
        <Text className={styles.empty} size={200}>
          Nothing to show here.
        </Text>
      )}

      {visibleJobs.length > 0 && (
        <div className={styles.list}>
          {visibleJobs.map((job) => {
            const expanded = !!expandedJobIds[job.jobId];
            const items = itemsByJobId[job.jobId] || [];

            return (
              <div className={isActive(job) ? styles.activeJobItem : styles.jobItem} key={job.jobId}>
                <div className={styles.jobHeader}>
                  <Button
                    appearance="transparent"
                    icon={expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                    size="small"
                    onClick={() => onToggleJob(job.jobId)}
                    aria-label={expanded ? "Hide job details" : "Show job details"}
                  />
                  <div className={styles.jobTitleBlock}>
                    <Text weight="semibold" size={200} className={styles.jobTitle}>
                      {getJobTitle(job)}
                    </Text>
                    <Text className={styles.mutedText}>Created {formatDate(job.createdAt)}</Text>
                  </div>
                  <Badge color={STATUS_COLOR[job.status]} size="small">
                    {STATUS_LABEL[job.status]}
                  </Badge>
                </div>

                <ProgressBar value={getProgress(job)} thickness="medium" />

                <div className={styles.countsRow}>
                  <Text size={200} className={styles.countPill}>
                    {job.processedCount}/{job.totalItems} done
                  </Text>
                  <Text size={200} className={styles.countPill}>
                    {job.remainingCount} left
                  </Text>
                  {job.failedCount > 0 && (
                    <Text size={200} className={styles.countPill}>
                      {job.failedCount} failed
                    </Text>
                  )}
                  <Text size={200} className={styles.countPill}>
                    {job.skippedCount} skipped
                  </Text>
                </div>

                {expanded && (
                  <div className={styles.itemList}>
                    {items.length === 0 && (
                      <Text className={styles.mutedText}>Loading email details...</Text>
                    )}
                    {items.map((item) => (
                      <div className={styles.itemRow} key={item.itemId}>
                        <Mail20Regular />
                        <Text size={200} className={styles.itemSubject} title={item.subject}>
                          {item.subject || "email"}
                        </Text>
                        <Badge color={ITEM_STATUS_COLOR[item.status]} size="small">
                          {item.status}
                        </Badge>
                        {item.errorMessage && (
                          <Text size={200} className={styles.itemError}>
                            {item.errorMessage}
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles.actions}>
                  {job.failedCount > 0 && (
                    <Button
                      size="small"
                      icon={<ArrowClockwise24Regular fontSize={14} />}
                      appearance="subtle"
                      onClick={() => onRetryJob(job.jobId)}
                    >
                      Retry failed
                    </Button>
                  )}
                  {!isTerminal(job.status) && (
                    <Button
                      size="small"
                      icon={<DismissCircle20Regular fontSize={14} />}
                      appearance="subtle"
                      onClick={() => onCancelJob(job.jobId)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
